/**
 * Coverage tab content for the Collection Detail page.
 *
 * Per-dimension expandable heatmap per `feature-spec.md` §2.6 — each
 * dimension card shows a grid of label cells coloured by support tier:
 *   high ≥ 15 / moderate 5–14 / low 1–4 / none = 0.
 *
 * Hover tooltip: `{labelName}: {count}`. Click a cell → drill-down
 * `Dialog` listing the dimension's full sorted label list. (Category
 * grouping deferred — wire shape doesn't carry category info; v1 ships
 * a flat sorted list, future enhancement can fetch the pinned taxonomy
 * version and map labelKey → category.)
 */

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../ui/tooltip';
import { cn } from '../../../../lib/utils';
import { getDimensionColor } from '../shared/dimensions';
import { useCollectionCoverage } from './hooks/useDatasets';
import type { Collection, DimensionCoverage } from './types';
import EmptyTabState from './components/EmptyTabState';

interface Props {
  collection: Collection;
}

type Tier = 'none' | 'low' | 'moderate' | 'high';

const tierOf = (count: number): Tier => {
  if (count === 0) return 'none';
  if (count < 5) return 'low';
  if (count < 15) return 'moderate';
  return 'high';
};

const TIER_CLASS: Record<Tier, string> = {
  none: 'bg-slate-100 text-slate-400',
  low: 'bg-amber-100 text-amber-900',
  moderate: 'bg-emerald-200 text-emerald-900',
  high: 'bg-emerald-600 text-white',
};

const CoverageTab: React.FC<Props> = ({ collection }) => {
  const { data, isLoading, isError, refetch } = useCollectionCoverage(
    collection.id,
    { enabled: collection.status === 'active' },
  );
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [drilldown, setDrilldown] = useState<DimensionCoverage | null>(null);

  if (collection.status !== 'active') {
    return <EmptyTabState tabName="coverage" />;
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="coverage-loading">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-32 animate-pulse rounded-lg border border-border/70 bg-card"
          />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-[40vh] flex-col items-center justify-center text-center">
        <p className="mb-4 text-muted-foreground">Failed to load coverage.</p>
        <Button variant="link" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="space-y-4" data-testid="coverage-tab">
      {data.staleDimensions.length > 0 && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900"
          data-testid="coverage-stale-banner"
        >
          Some dimensions are no longer in the pinned taxonomy:{' '}
          <span className="font-semibold">{data.staleDimensions.join(', ')}</span>
        </div>
      )}

      {data.perDimension.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          No coverage data yet.
        </p>
      ) : (
        data.perDimension.map((dim) => {
          const isCollapsed = collapsed.has(dim.dimension.key);
          return (
            <Card key={dim.dimension.key} data-testid={`coverage-dim-${dim.dimension.key}`}>
              <CardHeader>
                <button
                  type="button"
                  onClick={() => toggle(dim.dimension.key)}
                  aria-expanded={!isCollapsed}
                  className="flex w-full cursor-pointer items-center justify-between gap-3 text-left"
                  data-testid={`coverage-dim-toggle-${dim.dimension.key}`}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{ backgroundColor: getDimensionColor(dim.dimension.key) }}
                      aria-hidden="true"
                    />
                    <CardTitle className="text-sm font-semibold">
                      {dim.dimension.name}
                    </CardTitle>
                    <span className="text-[11px] text-muted-foreground">
                      ({dim.labels.length} labels)
                    </span>
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                      isCollapsed && 'rotate-180',
                    )}
                    aria-hidden="true"
                  />
                </button>
              </CardHeader>
              {!isCollapsed && (
                <CardContent>
                  {dim.labels.length === 0 ? (
                    <p className="text-[12px] text-muted-foreground">
                      No labels in this dimension.
                    </p>
                  ) : (
                    <div
                      className="flex flex-wrap gap-2"
                      data-testid={`coverage-cells-${dim.dimension.key}`}
                    >
                      {dim.labels.map((label) => {
                        const tier = tierOf(label.count);
                        return (
                          <Tooltip key={label.labelKey}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => setDrilldown(dim)}
                                data-testid={`coverage-cell-${dim.dimension.key}-${label.labelKey}`}
                                data-tier={tier}
                                className={cn(
                                  'inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded text-[12px] font-semibold tabular-nums transition hover:ring-2 hover:ring-primary/40',
                                  TIER_CLASS[tier],
                                )}
                                aria-label={`${label.labelName}: ${label.count}`}
                              >
                                {label.count.toLocaleString()}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {label.labelName}: {label.count.toLocaleString()}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })
      )}

      <Dialog open={drilldown !== null} onOpenChange={(open) => !open && setDrilldown(null)}>
        <DialogContent data-testid="coverage-drilldown">
          <DialogHeader>
            <DialogTitle>{drilldown?.dimension.name ?? ''}</DialogTitle>
            <DialogDescription>
              Labels in this dimension, sorted by count (highest first).
            </DialogDescription>
          </DialogHeader>
          {drilldown && (
            <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto">
              {[...drilldown.labels]
                .sort((a, b) => b.count - a.count)
                .map((label) => {
                  const tier = tierOf(label.count);
                  return (
                    <li
                      key={label.labelKey}
                      className="flex items-center justify-between gap-3 py-2 text-[13px]"
                      data-testid={`drilldown-row-${label.labelKey}`}
                    >
                      <span className="text-foreground">{label.labelName}</span>
                      <span
                        className={cn(
                          'rounded px-2 py-0.5 text-[11px] font-semibold tabular-nums',
                          TIER_CLASS[tier],
                        )}
                        data-tier={tier}
                      >
                        {label.count.toLocaleString()}
                      </span>
                    </li>
                  );
                })}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CoverageTab;
